import { OpenAPI } from "./core/OpenAPI";
import { request as __request } from "./core/request";

export interface Research {
  id: number;
  query: string;
  report: any;
  sources: any;
  status: string;
  created: string;
  modified: string;
  resource_type?: 'research';
  sort_date?: string;
}

export interface DailyInsightMessage {
  id: number;
  message_text: string;
  delivery_date: string;
  email_sent_at: string | null;
  created: string;
  modified: string;
  research_reports: Research[];
  resource_type?: 'daily_message';
  sort_date?: string;
}

export type ResourceItem = DailyInsightMessage | Research;

export class ResourcesService {
  /**
   * Get resources (daily messages and standalone research) with date filtering
   * @param limit Number of items to return (default 30)
   * @param before Timestamp to get items before this date
   * @param after Timestamp to get items after this date
   * @returns Array of resource items
   * @throws ApiError
   */
  public static async resourcesList(
    limit: number = 30,
    before?: string,
    after?: string
  ): Promise<ResourceItem[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (before) params.append('before', before);
    if (after) params.append('after', after);

    const result: unknown = await __request(OpenAPI, {
      method: 'GET',
      url: `/api/resources/?${params.toString()}`,
      errors: {
        401: `Unauthorized`,
      },
    });
    return result as ResourceItem[];
  }

  /**
   * Get a specific daily insight message
   * @param id
   * @returns DailyInsightMessage
   * @throws ApiError
   */
  public static async dailyMessageRetrieve(id: string): Promise<DailyInsightMessage> {
    const result: unknown = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/daily-messages/{id}/',
      path: {
        id: id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not found`,
      },
    });
    return result as DailyInsightMessage;
  }

  /**
   * Get a specific research report
   * @param id
   * @returns Research
   * @throws ApiError
   */
  public static async researchRetrieve(id: string): Promise<Research> {
    const result: unknown = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/web-research/{id}/',
      path: {
        id: id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not found`,
      },
    });
    return result as Research;
  }
}
